/**
 * User-friendly error message handler
 * Converts technical API errors into readable messages
 */

// Map of error codes/messages to user-friendly text
const errorMessages = {
  // Network errors
  'Network Error': 'Unable to connect. Please check your internet connection and try again.',
  'ERR_NETWORK': 'Unable to connect. Please check your internet connection and try again.',
  'ECONNABORTED': 'The request took too long. Please try again.',
  'timeout': 'The request timed out. Please try again.',
  
  // Authentication errors
  'Invalid credentials': 'The email or password you entered is incorrect.',
  'Invalid token': 'Your session has expired. Please log in again.',
  'Token expired': 'Your session has expired. Please log in again.',
  'User not found': 'No account found with this email address.',
  'Email already exists': 'An account with this email already exists. Try logging in instead.',
  'Email already registered': 'An account with this email already exists. Try logging in instead.',
  
  // Subscription errors
  'Subscription required': 'This feature requires an active subscription. Subscribe for just R25 (30 days access).',
  'Subscription expired': 'Your subscription has expired. Renew to continue using all features.',
  'Payment failed': 'Payment could not be processed. Please try again or use a different payment method.',
  'Invalid payment': 'There was an issue with your payment. Please contact support if this persists.',
  
  // CV errors
  'CV not found': 'This CV could not be found. It may have been deleted.',
  'CV limit reached': 'You\'ve reached your CV limit. Delete an existing CV or upgrade your plan.',
  'Invalid CV data': 'Some CV information is missing or invalid. Please check and try again.',
  
  // File upload errors
  'File too large': 'This file is too large. Please upload a file smaller than 10MB.',
  'Invalid file type': 'This file type is not supported. Please upload a PDF, JPG, or PNG file.',
  'Upload failed': 'File upload failed. Please try again.',
  
  // Rate limiting
  'Too many requests': 'You\'re making requests too quickly. Please wait a moment and try again.',
  'Rate limit exceeded': 'You\'ve reached the limit for this feature. Please wait a few minutes.',
  
  // AI errors
  'AI service unavailable': 'Our AI service is temporarily unavailable. Please try again in a few minutes.',
  'AI generation failed': 'We couldn\'t generate the content. Please try again.',
  
  // Generic server errors
  '500': 'Something went wrong on our end. Please try again later.',
  '502': 'Service temporarily unavailable. Please try again in a moment.',
  '503': 'Service temporarily unavailable. Please try again in a moment.',
  'Internal server error': 'Something went wrong. Please try again later.',
  
  // Validation errors
  'Invalid email': 'Please enter a valid email address.',
  'Password too weak': 'Password must be at least 8 characters with a number and special character.',
  'Required field': 'Please fill in all required fields.',
};

/**
 * Get a user-friendly error message from an API error
 * @param {Error|Object} error - The error object from axios or API
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Handle axios error structure
  if (error?.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    // Check for specific error message from API
    if (data?.detail) {
      const detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      // Look for matching user-friendly message
      for (const [key, message] of Object.entries(errorMessages)) {
        if (detail.toLowerCase().includes(key.toLowerCase())) {
          return message;
        }
      }
      // Return API message if no match (but clean it up)
      return cleanErrorMessage(detail);
    }
    
    // Check for status code match
    if (errorMessages[status.toString()]) {
      return errorMessages[status.toString()];
    }
    
    // Default status-based messages
    if (status === 401) return 'Please log in to continue.';
    if (status === 403) return 'You don\'t have permission to do this.';
    if (status === 404) return 'The requested resource was not found.';
    if (status === 422) return 'Please check your input and try again.';
    if (status >= 500) return 'Something went wrong on our end. Please try again later.';
  }
  
  // Handle network errors
  if (error?.code) {
    if (errorMessages[error.code]) {
      return errorMessages[error.code];
    }
  }
  
  // Handle error message string
  if (error?.message) {
    for (const [key, message] of Object.entries(errorMessages)) {
      if (error.message.toLowerCase().includes(key.toLowerCase())) {
        return message;
      }
    }
    return cleanErrorMessage(error.message);
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    for (const [key, message] of Object.entries(errorMessages)) {
      if (error.toLowerCase().includes(key.toLowerCase())) {
        return message;
      }
    }
    return cleanErrorMessage(error);
  }
  
  // Default fallback
  return 'Something went wrong. Please try again.';
};

/**
 * Clean up technical error messages for display
 */
const cleanErrorMessage = (message) => {
  if (!message) return 'Something went wrong. Please try again.';
  
  // Remove technical prefixes
  let cleaned = message
    .replace(/^Error:\s*/i, '')
    .replace(/^Exception:\s*/i, '')
    .replace(/^\[.*?\]\s*/, '')
    .trim();
  
  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  
  // Add period if missing
  if (!/[.!?]$/.test(cleaned)) {
    cleaned += '.';
  }
  
  return cleaned;
};

/**
 * Get error title based on error type
 */
export const getErrorTitle = (error) => {
  if (error?.response?.status === 401) return 'Authentication Required';
  if (error?.response?.status === 403) return 'Access Denied';
  if (error?.response?.status === 404) return 'Not Found';
  if (error?.response?.status === 429) return 'Too Many Requests';
  if (error?.response?.status >= 500) return 'Server Error';
  if (error?.code === 'ERR_NETWORK') return 'Connection Error';
  return 'Error';
};

export default getErrorMessage;
